/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { AttributesForSelectedVariantInput, getAttributesForSelectedVariant } from '@msdyn365-commerce-modules/product-specification';
import { validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { Drawer, IModuleProps, INodeProps, Module } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICipProductSpecificationData } from './cip-product-specification.data';
import { displayStyle, ICipProductSpecificationProps } from './cip-product-specification.props.autogenerated';
import { ProductSpecificationTableRow } from './components/cip-product-specification-table-stucture';

/**
 * Maintain Drawer State.
 */
export interface IProductSpecificationState {
    isDrawerOpen: boolean;
    isAdditionalMediaDrawerOpen: boolean;
    drawerListId: number | undefined;
}

/**
 * Product specification view props.
 */
export interface IProductSpecificationViewProps extends ICipProductSpecificationProps<{}> {
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: (JSX.Element | null)[];

    title?: React.ReactNode;
    isAccrodion?: boolean;
    handleText?(event: Msdyn365.ContentEditableEvent): void;
}

/**
 * Check for display style either accordion or table.
 */
export interface IProductSpecificationExtentedProps extends ICipProductSpecificationProps<ICipProductSpecificationData> {
    displayStyle?: displayStyle;
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<ICipProductSpecificationProps<ICipProductSpecificationData>>}
 */
@observer
class ProductSpecification extends React.Component<IProductSpecificationExtentedProps, IProductSpecificationState> {
    public constructor(props: IProductSpecificationExtentedProps) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            isAdditionalMediaDrawerOpen: false,
            drawerListId: undefined
        };
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        let data: ICipProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }

        const { heading, className } = this.props.config;
        const { productSpecificationData } = this.props.data;
        const isAccrodion = this.props.config.displayStyle === displayStyle.accordion;
        const viewProps = {
            ...this.props,
            handleText: this.handleHeadingChange,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: isAccrodion
                ? {
                      moduleProps: this.props,
                      className: classnames('ms-product-specification__accordion', className)
                  }
                : {
                      className: classnames('ms-product-specification__table table-striped table-bordered'),
                      tag: 'table'
                  },
            isAccrodion,
            ProductSpecificationTableBodyProps: isAccrodion
                ? {}
                : {
                      className: '',
                      tag: 'tbody'
                  },
            title: heading?.text && (
                <Msdyn365.Text
                    className='ms-product-specification__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            productSpecificationResult:
                productSpecificationData.result && (isAccrodion ? this._createDrawerBody(data) : this._createTableBody(data))
        } as IProductSpecificationViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _createTableBody(content: ICipProductSpecificationData): (JSX.Element | null)[] {
        const tableRows = content.productSpecificationData.result!.map((product, index) => {
            const cellData: JSX.Element | null = this._renderProductCell(product);

            if (!cellData) {
                return null;
            }

            return (
                <ProductSpecificationTableRow
                    className='ms-product-specification__table-row'
                    key={index}
                    productName={product.Name}
                    cellData={cellData}
                />
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            tableRows.push(this._createAdditionalMediaDrawer());
        }

        return tableRows;
    }

    private _createDrawerBody(content: ICipProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        const drawers = content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            drawers.push(this._createAdditionalMediaDrawer());
        }

        return drawers;
    }
    private _createAdditionalMediaDrawer(): JSX.Element {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        return (
            <Module {...accordionItemContainer} key={'additional_media_drawer'}>
                <Drawer
                    key={'additional_media_drawer'}
                    collapseProps={{
                        timeout: 0,
                        isOpen: this.state.isAdditionalMediaDrawerOpen
                    }}
                    className='ms-product-specification__drawer'
                    openGlyph='ms-product-specification__drawer-open'
                    closeGlyph='ms-product-specification__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={this.props.resources.additionalDownloadsText}
                    onToggle={() => {
                        this.setState(prevState => ({
                            ...prevState,
                            isAdditionalMediaDrawerOpen: !prevState.isAdditionalMediaDrawerOpen
                        }));
                    }}
                >
                    <div className='ms-product-details'>
                        <ul className='ms-product-details-additional-media'>
                            {(this.props.data.additionalMediaLocations.result || []).map(mediaLocation => (
                                <li key={mediaLocation.Uri}>
                                    <a href={mediaLocation.Uri} download>
                                        {mediaLocation.AltText}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Drawer>
            </Module>
        );
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        let cellContent: JSX.Element | null = null;

        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                cellContent = this._renderCurrencyCell(product);
                break;
            case 2: // DateTime
                cellContent = this._renderDateTimeCell(product);
                break;
            case 3: // Decimal
                cellContent = this._renderDecimalCell(product);
                break;
            case 4: // Integer
                cellContent = this._renderIntegerCell(product);
                break;
            case 5: // Text
                cellContent = this._renderTextCell(product);
                break;
            case 6: // TrueFalse
                cellContent = this._renderTrueFalseCell(product);
                break;
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }

        return cellContent;
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            const unitOfMeasure = product.UnitOfMeasureSymbol ? ` ${product.UnitOfMeasureSymbol}` : '';
            const currencyCode = product.CurrencyCode ?? '';
            return <span>{`${currencyCode}${product.CurrencyValue}${unitOfMeasure}`}</span>;
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            const unitOfMeasure = product.UnitOfMeasureSymbol ? ` ${product.UnitOfMeasureSymbol}` : '';
            return <span>{`${product.IntegerValue}${unitOfMeasure}`}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            const unitOfMeasure = product.UnitOfMeasureSymbol ? ` ${product.UnitOfMeasureSymbol}` : '';
            return <span>{`${product.FloatValue}${unitOfMeasure}`}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            // eslint-disable-next-line react/no-danger
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default ProductSpecification;
